import React, { useState, useEffect, useCallback } from "react";
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { Table, Button, Spinner, Alert, Form, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { fetchData, patchData } from "../../redux/slices/apiCallSlice";

function AdminJobVisibility() {
  const [jobs, setJobs] = useState([]); // State to store the job data
  const [loading, setLoading] = useState(false); // State for loading spinner
  const [error, setError] = useState(null); // State for errors
  useGuardedRealtimeList({ apiKey: 'generalProject', apply: setJobs, blocked: loading });
  const [searchTerm, setSearchTerm] = useState(""); // State for search input
  const [showHidden, setShowHidden] = useState(false); // State for toggling between hidden and visible jobs
  const apiUrls = useSelector(selectApiUrls); // Get API URLs from Redux
  const dispatch = useDispatch();
  
  // Function to fetch jobs from the API using Redux
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchData({ url: apiUrls.generalProject })).unwrap();
      setJobs(response); // Store all jobs in state
      setLoading(false);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to fetch jobs");
      setLoading(false);
    }
  }, [dispatch, apiUrls.generalProject]);

  // Fetch jobs when component mounts - runs only once
  useEffect(() => {
    fetchJobs(); // Fetch job details when component mounts
  }, [fetchJobs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle toggling the HideFromList value
  const handleToggleVisibility = async (ID, currentVisibility) => {
    try {
      await dispatch(patchData({
        url: apiUrls.generalProject,
        data: {
          ID,
          HideFromList: String(!currentVisibility),
        }
      })).unwrap();
      
      // Update the local job state with the new value for HideFromList
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.ID === ID ? { ...job, HideFromList: !currentVisibility } : job
        )
      );
    } catch (error) {
      console.error("Error updating job visibility:", error);
    }
  };

  // Filter jobs based on search term and the visibility toggle
  const filteredJobs = jobs.filter((job) => {
    const matchesSearchTerm =
      job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase());

    const isVisible = showHidden
      ? job.HideFromList === true
      : job.HideFromList === false || job.HideFromList === null;

    return matchesSearchTerm && isVisible;
  });

  return (
    <div className="m-4">
      {loading ? (
        <Spinner animation="border" />
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <h3>Total Jobs: {jobs.length}</h3>
          <div className="d-flex justify-content-center">
            <h2>Show/ Hide Jobs</h2>
          </div>
          <div>
            <Row className="d-flex">
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by Job Number or Project Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mb-3"
                />
              </Col>
              <Col>
                <Form.Check
                  type="switch"
                  id="custom-switch"
                  label={showHidden ? "Show Visible Jobs" : "Show Hidden Jobs"}
                  checked={showHidden}
                  onChange={() => setShowHidden((prev) => !prev)}
                  className="mb-3"
                />
              </Col>
            </Row>
          </div>

          {/* Toggle for showing hidden jobs */}
          <div className="limit-height">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>SaleQuotesID</th>
                  <th>Job Number</th>
                  <th>Project Name</th>
                  <th>Visibility</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job.ID}>
                    <td>{job.ID}</td>
                    <td>{job.SalesQuoteID}</td>
                    <td>{job.JobNumber}</td>
                    <td>{job.ProjectName}</td>
                    <td>
                      <Button
                        variant={job.HideFromList ? "danger" : "success"}
                        onClick={() =>
                          handleToggleVisibility(job.ID, job.HideFromList)
                        }
                      >
                        {job.HideFromList ? "Show" : "Hide"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminJobVisibility;
